import { Box, Button, Stack, Typography } from "@mui/material";
import { HandleGateDialog } from "Components/Social/HandleGateDialog";
import { MutualFollowConfirmDialog } from "Components/Social/MutualFollowConfirmDialog";
import { SocialUserActionsMenu } from "Components/Social/SocialUserActionsMenu";
import { useCanInitiateChatWith } from "Hooks/Chat/useCanInitiateChatWith";
import { useSocialPermissions } from "Hooks/Social/useSocialPermissions";
import { useViewerDisclosure } from "Hooks/Social/useViewerDisclosure";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { PublicProfileDto } from "Infrastructure/Api/Api";
import { ProfileFollowButtons } from "Pages/PublicProfile/ProfileFollowButtons";
import { SendMessageButton } from "Pages/PublicProfile/SendMessageButton";
import { useProfileGraphActions } from "Pages/PublicProfile/useProfileGraphActions";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";

type Props = {
  profile: PublicProfileDto;
};

/**
 * The social actions row of the public profile: the follow buttons, Send message, and the kebab
 * — or only Unblock when the viewer blocked this persona, since every graph button would be a
 * dead end server-side anyway. What the viewer may do comes from useSocialPermissions; the
 * HandleGate dialog intercepts actions of a handle-less caller. Hidden on the viewer's own
 * profile; disabled placeholders for anonymous visitors. Actions that complete a mutual follow
 * are confirmed first (useProfileGraphActions).
 */
export const ProfileActions = ({ profile }: Props) => {
  const [isGateOpen, setIsGateOpen] = useState(false);
  const { t } = useResource();
  // `user` only decides disabled state and the actions menu; every rule lives in the hooks.
  const user = useLoggedInUser();
  const { canFollow, isLoading, needsHandle, isSelf, relationship } =
    useSocialPermissions(profile);
  const { disclosesName } = useViewerDisclosure();
  // A business persona never starts a conversation with a customer (App ADR-0015).
  const canInitiateChatWith = useCanInitiateChatWith();
  const {
    follow,
    unfollow,
    approve,
    deleteRequest,
    unblock,
    isMutating,
    mutualConfirmDialogProps,
  } = useProfileGraphActions(profile, relationship);

  if (isSelf) {
    return null;
  }

  // The gate fires client-side for a smoother path; the backend enforces it too.
  const guarded = (action: () => void) => () =>
    needsHandle ? setIsGateOpen(true) : action();

  const isBlocked = !!relationship?.isBlocked;

  // Sending a message discloses the sender's name (App ADR-0011). A mutual follower already
  // sees it, so only a private viewer who is not yet mutual is told — and only when the
  // disclosure happens at all: Full Moone on both sides (product matrix 2026-10-21).
  const isSendMessageDisclosingName =
    disclosesName &&
    profile.isFullMoone &&
    !relationship?.isMutual &&
    !isBlocked;

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={2}
        mt={1.5}
        justifyContent="center"
        alignItems={{ xs: "stretch", sm: "center" }}
        alignSelf="stretch"
        sx={{ "& .MuiButton-root": { whiteSpace: "nowrap" } }}
      >
        {isBlocked ? (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={isMutating}
            onClick={unblock}
          >
            {t(Resources.Social.People.Unblock)}
          </Button>
        ) : (
          <>
            <ProfileFollowButtons
              relationship={relationship}
              canFollow={canFollow}
              isLoading={isLoading}
              isAnonymous={!user}
              isMutating={isMutating}
              guarded={guarded}
              onFollow={follow}
              onUnfollow={unfollow}
              onApprove={approve}
              onDeleteRequest={deleteRequest}
            />
            {canInitiateChatWith(profile.accessRoleCode) && (
              <SendMessageButton
                profile={profile}
                isDisabled={!user || isMutating}
                guarded={guarded}
              />
            )}
          </>
        )}
        {!!user && (
          // The stack stretches the text buttons to full width on phones; the kebab must keep
          // its intrinsic size or its round hover ripple stretches into a full-width ellipse.
          <Box alignSelf="center">
            <SocialUserActionsMenu
              identityUserPublicID={profile.identityUserPublicID}
              handle={profile.handle}
              displayName={profile.displayName}
              isBlockVisible={!isBlocked}
              isDisabled={isMutating}
              needsHandle={needsHandle}
              onHandleGate={() => setIsGateOpen(true)}
            />
          </Box>
        )}
      </Stack>
      {isSendMessageDisclosingName && (
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          mt={1}
        >
          {t(Resources.Chat.NewChat.PrivateSenderNotice)}
        </Typography>
      )}
      <HandleGateDialog
        isOpen={isGateOpen}
        onClose={() => setIsGateOpen(false)}
      />
      <MutualFollowConfirmDialog {...mutualConfirmDialogProps} />
    </>
  );
};
